import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AddPhotoAlternate as AddPhotoIcon,
  VideoCall as VideoCallIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import {
  createTutorialVideo,
  updateTutorialVideo,
  uploadTutorialFile,
  buildImageUrl,
  getVideoThumbnailFallback,
} from "../../services/tutorialVideoService";
import { listHelpCategories } from "../../services/helpCategoryService";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 620,
    maxWidth: 860,
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  uploadBox: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    transition: "border-color 0.2s",
    marginBottom: theme.spacing(2),
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  previewBox: {
    position: "relative",
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
  },
  previewImg: {
    width: "100%",
    maxHeight: 200,
    objectFit: "contain",
    display: "block",
    backgroundColor: "#f5f5f5",
  },
  previewVideo: {
    width: "100%",
    maxHeight: 200,
    display: "block",
    backgroundColor: "#111",
  },
  removeBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    backgroundColor: "rgba(0,0,0,0.6)",
    color: "#fff",
    padding: 4,
    "&:hover": { backgroundColor: "rgba(200,0,0,0.8)" },
  },
  characterCount: {
    textAlign: "right",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: -theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  sectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
    marginBottom: 6,
    marginTop: theme.spacing(1),
  },
  uploadingBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1, 2),
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(2),
  },
  tabsRoot: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
  },
  tab: {
    minHeight: 40,
    fontSize: "0.8rem",
  },
}));

const TutorialVideoModal = ({
  open,
  onClose,
  tutorialVideo = null,
  onSuccess,
}) => {
  const classes = useStyles();
  const videoFileRef = useRef(null);
  const imageFileRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [mediaType, setMediaType] = useState("youtube"); // "youtube" | "video" | "image"
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    videoUrl: "",
    thumbnailUrl: "",
    imageUrl: "",
    category: "",
  });

  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  const isEditing = !!tutorialVideo;

  useEffect(() => {
    if (!open) return;
    listHelpCategories()
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (tutorialVideo) {
      const storedVideo = tutorialVideo.videoUrl || "";
      const isLocalVideo = storedVideo && !storedVideo.startsWith("http");
      const hasImage = !!tutorialVideo.imageUrl && !storedVideo;
      const detectedType = hasImage ? "image" : isLocalVideo ? "video" : "youtube";
      setMediaType(detectedType);
      setFormData({
        title: tutorialVideo.title || "",
        description: tutorialVideo.description || "",
        videoUrl: storedVideo,
        thumbnailUrl: tutorialVideo.thumbnailUrl || "",
        imageUrl: tutorialVideo.imageUrl || "",
        category: tutorialVideo.category || "",
      });
      setImagePreviewUrl(
        tutorialVideo.imageUrl ? buildImageUrl(tutorialVideo.imageUrl) : null
      );
      setVideoPreviewUrl(
        isLocalVideo
          ? `${process.env.REACT_APP_BACKEND_URL || ""}${storedVideo}`
          : null
      );
    } else {
      resetForm();
    }
    setVideoFile(null);
    setImageFile(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialVideo, open]);

  const resetForm = () => {
    setFormData({ title: "", description: "", videoUrl: "", thumbnailUrl: "", imageUrl: "", category: "" });
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setImageFile(null);
    setImagePreviewUrl(null);
    setMediaType("youtube");
  };

  const handleMediaTypeChange = (_, newType) => {
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setImageFile(null);
    setImagePreviewUrl(null);
    if (videoFileRef.current) videoFileRef.current.value = "";
    if (imageFileRef.current) imageFileRef.current.value = "";
    setFormData((prev) => ({ ...prev, videoUrl: "", imageUrl: "", thumbnailUrl: "" }));
    setMediaType(newType);
  };

  const handleField = (field) => (e) => {
    const v = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: v }));
  };

  // ── Vídeo upload ──────────────────────────────────────────
  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Selecione apenas arquivos de vídeo");
      return;
    }
    setVideoFile(file);
    setVideoPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveVideo = () => {
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setFormData((prev) => ({ ...prev, videoUrl: "" }));
    if (videoFileRef.current) videoFileRef.current.value = "";
  };

  // ── Imagem upload ─────────────────────────────────────────
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione apenas arquivos de imagem");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem deve ter no máximo 5 MB");
      return;
    }
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreviewUrl(null);
    setFormData((prev) => ({ ...prev, imageUrl: "" }));
    if (imageFileRef.current) imageFileRef.current.value = "";
  };

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Título é obrigatório");
      return;
    }

    const hasYoutubeUrl = mediaType === "youtube" && !!formData.videoUrl.trim();
    const hasVideoFile  = mediaType === "video" && (!!videoFile || !!formData.videoUrl);
    const hasImageFile  = mediaType === "image" && (!!imageFile || !!formData.imageUrl);

    if (!hasYoutubeUrl && !hasVideoFile && !hasImageFile) {
      toast.error("Adicione uma mídia: URL do YouTube, vídeo ou imagem");
      return;
    }

    setLoading(true);
    try {
      let finalVideoUrl = mediaType === "youtube" ? formData.videoUrl?.trim() || undefined : formData.videoUrl;
      let finalImageUrl = formData.imageUrl;

      if (mediaType === "video" && videoFile) {
        setUploadingVideo(true);
        try {
          finalVideoUrl = await uploadTutorialFile(videoFile);
        } catch {
          toast.error("Erro ao enviar vídeo — verifique o servidor");
          return;
        } finally {
          setUploadingVideo(false);
        }
      }

      if (mediaType === "image" && imageFile) {
        setUploadingImage(true);
        try {
          finalImageUrl = await uploadTutorialFile(imageFile);
        } catch {
          toast.error("Erro ao enviar imagem — verifique o servidor");
          return;
        } finally {
          setUploadingImage(false);
        }
      }

      const isYoutube = mediaType === "youtube";
      const payload = {
        title: formData.title.trim(),
        description: formData.description?.trim() || undefined,
        videoUrl: mediaType !== "image" ? (finalVideoUrl || undefined) : undefined,
        thumbnailUrl:
          formData.thumbnailUrl?.trim() ||
          (isYoutube ? getVideoThumbnailFallback(finalVideoUrl) : undefined) ||
          undefined,
        imageUrl: mediaType === "image" ? (finalImageUrl || undefined) : undefined,
        category: formData.category?.trim() || undefined,
      };

      let response;
      if (isEditing) {
        response = await updateTutorialVideo(tutorialVideo.id, payload);
        toast.success("Item atualizado com sucesso!");
      } else {
        response = await createTutorialVideo(payload);
        toast.success("Item criado com sucesso!");
      }

      onSuccess(response.data);
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.error || "Erro ao salvar item");
    } finally {
      setLoading(false);
      setUploadingVideo(false);
      setUploadingImage(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h6">
          {isEditing ? "Editar Item de Ajuda" : "Novo Item de Ajuda"}
        </Typography>
        <IconButton onClick={handleClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Título */}
        <TextField
          label="Título *"
          variant="outlined"
          fullWidth
          value={formData.title}
          onChange={handleField("title")}
          className={classes.formField}
          disabled={loading}
          inputProps={{ maxLength: 255 }}
        />

        {/* Categoria (Select das categorias criadas) */}
        <FormControl variant="outlined" fullWidth className={classes.formField}>
          <InputLabel>Categoria</InputLabel>
          <Select
            value={formData.category}
            onChange={handleField("category")}
            label="Categoria"
            disabled={loading}
          >
            <MenuItem value="">
              <em>Sem categoria</em>
            </MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.name}>
                {cat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {categories.length === 0 && (
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: -12, marginBottom: 16 }}>
            Nenhuma categoria criada. Crie categorias na tela de administração antes de vincular.
          </Typography>
        )}

        {/* Texto complementar */}
        <TextField
          label="Texto complementar"
          variant="outlined"
          fullWidth
          multiline
          minRows={4}
          value={formData.description}
          onChange={handleField("description")}
          className={classes.formField}
          disabled={loading}
          placeholder="Descreva o conteúdo ou adicione instruções complementares..."
        />
        <Typography className={classes.characterCount}>
          {formData.description ? formData.description.length : 0} caracteres
        </Typography>

        {/* ── Mídia — apenas uma das três opções ──────────── */}
        <Typography className={classes.sectionLabel}>Mídia</Typography>
        <Tabs
          value={mediaType}
          onChange={handleMediaTypeChange}
          className={classes.tabsRoot}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value="youtube" label="URL YouTube" className={classes.tab} disabled={loading} />
          <Tab value="video"   label="Upload Vídeo" className={classes.tab} disabled={loading} />
          <Tab value="image"   label="Upload Imagem" className={classes.tab} disabled={loading} />
        </Tabs>

        {/* ── URL YouTube ── */}
        {mediaType === "youtube" && (
          <TextField
            label="URL do YouTube"
            variant="outlined"
            fullWidth
            value={formData.videoUrl}
            onChange={handleField("videoUrl")}
            className={classes.formField}
            disabled={loading}
            placeholder="https://www.youtube.com/watch?v=..."
          />
        )}

        {/* ── Upload Vídeo ── */}
        {mediaType === "video" && (
          <>
            {uploadingVideo && (
              <Box className={classes.uploadingBox}>
                <CircularProgress size={18} />
                <Typography variant="body2">Enviando vídeo...</Typography>
              </Box>
            )}

            {videoPreviewUrl ? (
              <Box className={classes.previewBox}>
                <video src={videoPreviewUrl} className={classes.previewVideo} controls />
                <IconButton size="small" className={classes.removeBtn} onClick={handleRemoveVideo} disabled={loading}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box className={classes.uploadBox} onClick={() => !loading && videoFileRef.current?.click()}>
                <VideoCallIcon style={{ fontSize: 36, opacity: 0.4 }} />
                <Typography variant="body2" color="textSecondary">
                  Clique para selecionar um vídeo (MP4, WebM, etc.)
                </Typography>
              </Box>
            )}

            <input ref={videoFileRef} type="file" accept="video/*" style={{ display: "none" }} onChange={handleVideoSelect} />
          </>
        )}

        {/* ── Upload Imagem ── */}
        {mediaType === "image" && (
          <>
            {uploadingImage && (
              <Box className={classes.uploadingBox}>
                <CircularProgress size={18} />
                <Typography variant="body2">Enviando imagem...</Typography>
              </Box>
            )}

            {imagePreviewUrl ? (
              <Box className={classes.previewBox}>
                <img src={imagePreviewUrl} alt="Preview" className={classes.previewImg} />
                <IconButton size="small" className={classes.removeBtn} onClick={handleRemoveImage} disabled={loading}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box className={classes.uploadBox} onClick={() => !loading && imageFileRef.current?.click()}>
                <AddPhotoIcon style={{ fontSize: 36, opacity: 0.4 }} />
                <Typography variant="body2" color="textSecondary">
                  Clique para selecionar uma imagem (máx. 5 MB)
                </Typography>
              </Box>
            )}

            <input ref={imageFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageSelect} />
          </>
        )}
      </DialogContent>

      <DialogActions style={{ padding: 16, borderTop: "1px solid #e0e0e0" }}>
        <Button onClick={handleClose} disabled={loading} startIcon={<CancelIcon />}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !formData.title.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? "Salvando..." : isEditing ? "Atualizar" : "Criar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialVideoModal;
